/**
 * Snapshot lesen — die Gegenseite zu snapshot.ts.
 *
 * `snapshot.ts` BAUT die Zeile (der Cron schreibt sie), dieses Modul LIEST
 * sie. Die Trennung ist Absicht: Der Browser hat weder Schreibrecht (RLS) noch
 * Grund, einen Zustand zu berechnen — er zeigt an, was in der Tagesakte steht.
 *
 * DREI DINGE MACHT DIESES MODUL, UND NUR DIESE DREI
 * 1. Zeilen holen, die zur HEUTE GELTENDEN Regel gehören.
 * 2. Je Symbol die neueste behalten.
 * 3. Sagen, wie alt sie ist — damit die Oberfläche eine vier Wochen alte
 *    Zeile nicht als „heute" ausgibt.
 *
 * WARUM DER KONSTANTEN-HASH MITGEPRÜFT WIRD
 * Eine Zeile mit `strategy_version = 1.2`, aber anderem Hash stammt aus einem
 * Lauf, in dem eine Schwelle verändert wurde, ohne die Version zu erhöhen.
 * Sie SIEHT aus wie die aktuelle Regel und ist es nicht. Solche Zeilen werden
 * hier verworfen statt angezeigt — lieber „noch nicht bewertet" als eine Zahl,
 * die aus einer anderen Regel stammt.
 */

import { KONSTANTEN_HASH, REGEL_ID, REGEL_VERSION, type SetupState } from './setup-state';

/**
 * Ab wann ein Snapshot nicht mehr als Tagesstand durchgeht.
 *
 * Vier Tage decken ein langes Wochenende plus einen ausgefallenen Lauf ab.
 * Alles darüber ist ein Betriebsproblem und soll als solches sichtbar werden,
 * nicht als stille alte Zahl.
 */
export const MAX_SNAPSHOT_ALTER_TAGE = 4;

export interface SnapshotGelesen {
  symbol: string;
  as_of_date: string;
  computed_at: string | null;
  strategy_id: string;
  strategy_version: string;
  konstanten_hash: string;
  schema_version: string;
  daten_quelle: string | null;
  letzte_bar_datum: string | null;
  datenalter_tage: number | null;
  data_completeness: 'complete' | 'incomplete';
  zustand: SetupState['zustand'];
  zustand_text: string | null;
  ausloeser: SetupState['ausloeser'];
  tag_von: number | null;
  tradeable: boolean;
  blockiert_durch: string[];
  gruende: string[];
  bedingungen: Array<{ id: string; text: string; erfuellt: boolean | null }>;
  missing_conditions: Array<{ id: string; text: string }>;
  bedingungen_erfuellt: number;
  bedingungen_gesamt: number;
  naechste_pruefung: string | null;
  entry: number | null;
  limit_bis: number | null;
  trigger_ab: number | null;
  initial_stop: number | null;
  exit_profil: string | null;
  marktregime: string | null;
  portfolio_risk_check: {
    max_korrelation: number;
    gegen: string;
    fenster_tage: number;
    gegen_anzahl: number;
  } | null;
  kennzahlen: SetupState['kennzahlen'] | null;
  alert_event_id: string | null;
}

/**
 * Gehört diese Zeile zu der Regel, nach der heute gehandelt wird?
 *
 * Alle drei Teile müssen stimmen. Der Hash ist der strengste davon — er
 * erwischt genau den Fehler, den eine Versionsnummer allein nicht erwischt.
 */
export function passtZurRegel(row: {
  strategy_id: string;
  strategy_version: string;
  konstanten_hash: string;
}): boolean {
  return (
    row.strategy_id === REGEL_ID &&
    row.strategy_version === REGEL_VERSION &&
    row.konstanten_hash === KONSTANTEN_HASH
  );
}

/**
 * Je Symbol die neueste Zeile.
 *
 * Neuer heißt: späteres `as_of_date`. Bei gleichem Tag entscheidet
 * `computed_at` — ein zweiter Lauf desselben Tages hat die Zeile per Upsert
 * ohnehin überschrieben, aber die Reihenfolge aus der Datenbank ist nichts,
 * worauf man sich verlassen sollte.
 */
export function neuesteJeSymbol<T extends { symbol: string; as_of_date: string; computed_at?: string | null }>(
  rows: readonly T[]
): Map<string, T> {
  const map = new Map<string, T>();
  for (const row of rows) {
    const key = row.symbol.toUpperCase();
    const vorhanden = map.get(key);
    if (!vorhanden) {
      map.set(key, row);
      continue;
    }
    if (row.as_of_date > vorhanden.as_of_date) {
      map.set(key, row);
    } else if (
      row.as_of_date === vorhanden.as_of_date &&
      (row.computed_at ?? '') > (vorhanden.computed_at ?? '')
    ) {
      map.set(key, row);
    }
  }
  return map;
}

/**
 * Trägt die Zeile überhaupt einen Bedingungs-Fortschritt?
 *
 * Zeilen aus Läufen vor der Migration vom 11.08. tragen 0 von 0 und eine
 * leere Bedingungsliste. Sie wussten es nicht — die Oberfläche darf daraus
 * kein „alles erfüllt" machen.
 */
export function fortschrittLesbar(row: Pick<SnapshotGelesen, 'bedingungen_gesamt'>): boolean {
  return row.bedingungen_gesamt > 0;
}

/**
 * „Fast reif" = Kandidat, dem NUR noch der Auslöser fehlt.
 *
 * Dieselbe enge Definition wie `istFastReif` in bedingungen.ts, nur auf der
 * gelesenen Zeile statt auf dem gerechneten Zustand. Wäre sie weiter gefasst,
 * entstünde eine zweite Watchlist statt einer Vorauswahl.
 */
export function istFastReifLautSnapshot(
  row: Pick<SnapshotGelesen, 'zustand' | 'bedingungen_gesamt' | 'missing_conditions'>
): boolean {
  if (row.zustand !== 'boden_reift') return false;
  if (!fortschrittLesbar(row)) return false;
  const offen = (row.missing_conditions ?? []).map((m) => m.id);
  return offen.length === 1 && offen[0] === 'ausloeser';
}

/** Alter in Kalendertagen zwischen Snapshot-Tag und Bewertungstag. */
export function snapshotAlterTage(asOf: string, heute: string): number {
  const a = Date.parse(`${asOf}T00:00:00Z`);
  const b = Date.parse(`${heute}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.POSITIVE_INFINITY;
  return Math.round((b - a) / 86_400_000);
}

/**
 * Taugt der Snapshot als heutiger Stand?
 *
 * Bewusst getrennt von `tradeable`: Ein Snapshot kann frisch UND gesperrt
 * sein (veraltete Kursdaten am Tag des Laufs). Hier geht es nur um die Frage,
 * ob die AKTE aktuell ist — nicht darum, was in ihr steht.
 */
export function istSnapshotFrisch(
  row: { as_of_date: string },
  heute: string,
  maxTage = MAX_SNAPSHOT_ALTER_TAGE
): boolean {
  return snapshotAlterTage(row.as_of_date, heute) <= maxTage;
}

/**
 * Aus rohen Tabellenzeilen wird die Auswahl, mit der die Oberfläche arbeitet:
 * nur die heutige Regel, je Symbol die neueste Zeile.
 *
 * Ohne Datenbankzugriff — der liegt im Hook (`use-snapshots.ts`). Diese
 * Trennung hält die Entscheidung „welche Zeile gilt" prüfbar; sie ist der
 * Teil, der falsch sein kann.
 */
export function waehleSnapshots(rows: readonly SnapshotGelesen[]): Map<string, SnapshotGelesen> {
  return neuesteJeSymbol(rows.filter(passtZurRegel));
}

/** Zustände, die heute einen Blick verdienen. Alles andere ist Ruhe. */
const BEACHTENSWERT: ReadonlySet<string> = new Set(['zone_offen', 'boden_reift']);

export interface RadarAbgang {
  symbol: string;
  /** Der Zustand, in dem der Wert vorher war. */
  von: SetupState['zustand'];
  /** Der Zustand, in dem er jetzt ist. */
  nach: SetupState['zustand'];
  /** Erster Tag, an dem der neue Zustand in der Akte steht. */
  seit: string;
  /** Die Begründung der Zustandsmaschine vom Tag des Wechsels. */
  gruende: string[];
}

/**
 * Bereich D des Zielbilds: Was ist aus dem Radar gefallen?
 *
 * Ohne diesen Bereich verschwindet ein Wert lautlos aus der Liste, und man
 * merkt es erst, wenn man ihn sucht. Ein Signal, das ausläuft, ist aber ein
 * Ergebnis — und ein Ergebnis, das niemand sieht, kann niemanden etwas lehren.
 *
 * WAS ZÄHLT ALS ABGANG: der Weg von `zone_offen`/`boden_reift` in irgendetwas
 * anderes. Der umgekehrte Weg ist kein Abgang, sondern der Normalbetrieb der
 * Bereiche A und B. Und ein Wert, der nie beachtenswert war, kann auch nicht
 * herausfallen.
 *
 * Rein aus gespeicherten Zeilen abgeleitet — es wird nichts nachgerechnet.
 */
export function radarAbgaenge(
  rows: readonly SnapshotGelesen[],
  heute: string,
  maxTage = 7
): RadarAbgang[] {
  const jeSymbol = new Map<string, SnapshotGelesen[]>();
  for (const row of rows.filter(passtZurRegel)) {
    const key = row.symbol.toUpperCase();
    const liste = jeSymbol.get(key);
    if (liste) liste.push(row);
    else jeSymbol.set(key, [row]);
  }

  const abgaenge: RadarAbgang[] = [];
  for (const [symbol, liste] of jeSymbol) {
    // Aufsteigend, damit „vorher" und „jetzt" auch dann stimmen, wenn die
    // Datenbank die Zeilen in beliebiger Reihenfolge liefert.
    const sortiert = [...liste].sort((a, b) => a.as_of_date.localeCompare(b.as_of_date));
    const jetzt = sortiert[sortiert.length - 1];
    if (!jetzt || BEACHTENSWERT.has(jetzt.zustand)) continue;

    // Rückwärts bis zum letzten Tag mit anderem Zustand.
    let i = sortiert.length - 2;
    while (i >= 0 && sortiert[i]?.zustand === jetzt.zustand) i--;
    const vorher = i >= 0 ? sortiert[i] : undefined;
    const ersteNeue = sortiert[i + 1];
    if (!vorher || !ersteNeue) continue;
    if (!BEACHTENSWERT.has(vorher.zustand)) continue;
    if (snapshotAlterTage(ersteNeue.as_of_date, heute) > maxTage) continue;

    abgaenge.push({
      symbol,
      von: vorher.zustand,
      nach: jetzt.zustand,
      seit: ersteNeue.as_of_date,
      gruende: ersteNeue.gruende ?? [],
    });
  }

  // Der jüngste Abgang zuerst — er ist der, der noch überrascht.
  return abgaenge.sort((a, b) => b.seit.localeCompare(a.seit) || a.symbol.localeCompare(b.symbol));
}

/**
 * Der Snapshot in der Form, die die Oberflächen bisher vom Zustand kennen.
 *
 * Damit kann eine Karte, die heute `SetupState` erwartet, ohne Umbau auf die
 * gelesene Zeile umgestellt werden. Seit der Migration vom 11.08. tragt die
 * Zeile auch `zustand_text`, `ausloeser` und `tag_von` — die Umwandlung
 * verliert damit nichts mehr. Zeilen aus frueheren Laeufen haben diese Felder
 * nicht; dort bleibt der Satz leer, statt einen zu erfinden.
 */
export function alsZustand(row: SnapshotGelesen): SetupState {
  return {
    zustand: row.zustand,
    text: row.zustand_text ?? '',
    ausloeser: row.ausloeser ?? null,
    limit_bis: row.limit_bis,
    ungueltig_unter: row.initial_stop,
    trigger_ab: row.trigger_ab,
    tag_von: row.tag_von,
    gruende: row.gruende ?? [],
    as_of: row.as_of_date,
    datenalter_tage: row.datenalter_tage,
    tradeable: row.tradeable,
    blockiert_durch: (row.blockiert_durch ?? []) as SetupState['blockiert_durch'],
    kennzahlen:
      row.kennzahlen ?? {
        dd52_pct: null,
        atr_quotient: null,
        ueber_ma50: false,
        roc5_pct: null,
        atr14: null,
        squeeze: null,
        rs20_pp: null,
        tief_alter_bars: null,
      },
  } as SetupState;
}
